import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { sincronizacaoService, Sincronizacao } from '../../services/sincronizacao';
import SelectFuncionario from '../funcionario/SelectFuncionario';
import SelectTipoSincronizacao from './SelectTipoSincronizacao';

const SincronizacaoTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [idFuncionario, setIdFuncionario] = useState<number | null>(null);
  const [tipoSincronizacao, setTipoSincronizacao] = useState<number | null>(null);
  const [dataInicio, setDataInicio] = useState<Date | undefined>(undefined);
  const [dataFim, setDataFim] = useState<Date | undefined>(undefined);
  const [sincronizacoes, setSincronizacoes] = useState<Sincronizacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({
    open: false,
    message: '',
    severity: 'error',
  });

  // Filtrar sincronizações baseado na busca
  const filteredSincronizacoes = useMemo(() => {
    if (!search.trim()) return sincronizacoes;

    const searchLower = search.toLowerCase();
    return sincronizacoes.filter((sinc) =>
      sinc.id?.toString().includes(searchLower) ||
      sinc.promotor?.toLowerCase().includes(searchLower) ||
      sinc.dataString?.toLowerCase().includes(searchLower) ||
      sinc.tipoComandoString?.toLowerCase().includes(searchLower) ||
      sinc.tipoEntidadeString?.toLowerCase().includes(searchLower) ||
      sinc.situacaoSincronizacaoString?.toLowerCase().includes(searchLower)
    );
  }, [sincronizacoes, search]);

  // Paginação dos dados filtrados
  const paginatedSincronizacoes = useMemo(() => {
    return filteredSincronizacoes.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredSincronizacoes, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVisualizar = (id: number) => {
    navigate(`/Sincronizacao/visualizar/${id}`);
  };

  const handlePesquisar = async () => {
    if (!dataInicio || !dataFim) {
      setSnackbar({
        open: true,
        message: 'Data início e data final são obrigatórias',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const dataInicioFormatada = dataInicio.toISOString().split('T')[0];
      const dataFimFormatada = dataFim.toISOString().split('T')[0];
      
      const result = await sincronizacaoService.getSincronizacoesComFiltro(
        idFuncionario,
        tipoSincronizacao,
        dataInicioFormatada,
        dataFimFormatada
      );
      setSincronizacoes(result);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao buscar sincronizações',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filtros */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: '25%' }}>
            <SelectFuncionario
              value={idFuncionario || 0}
              onChange={(value) => setIdFuncionario(value === 0 ? null : value)}
              label="Funcionário"
              fullWidth={true}
            />
          </Box>
          <Box sx={{ minWidth: '25%' }}>
            <SelectTipoSincronizacao
              value={tipoSincronizacao}
              onChange={setTipoSincronizacao}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data início"
              value={dataInicio}
              onChange={(newValue) => setDataInicio(newValue || undefined)}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
            <DatePicker
              label="Data final"
              value={dataFim}
              onChange={(newValue) => setDataFim(newValue || undefined)}
              minDate={dataInicio}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
          </LocalizationProvider>
          <Button
            sx={{ minWidth: '11.7%' }}
            variant="outlined"
            onClick={handlePesquisar}
            disabled={loading}
            startIcon={<SearchIcon />}
          >
            Pesquisar
          </Button>
        </Box>
      </Box>

      {/* Busca */}
      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          placeholder="Filtrar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 200, width: '100%' }}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }
          }}
        />
      </Box>

      {/* Tabela */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Promotor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data inclusão</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo de comando</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo de entidade</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Situação</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSincronizacoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : search ? 'Nenhuma sincronização encontrada para esta busca.' : 'Nenhuma sincronização encontrada.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSincronizacoes.map((sinc) => (
                <TableRow key={sinc.id} hover>
                  <TableCell>{sinc.id}</TableCell>
                  <TableCell>{sinc.promotor}</TableCell>
                  <TableCell>{sinc.dataString}</TableCell>
                  <TableCell>{sinc.tipoComandoString}</TableCell>
                  <TableCell>{sinc.tipoEntidadeString}</TableCell>
                  <TableCell>{sinc.situacaoSincronizacaoString}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handleVisualizar(sinc.id)}
                    >
                      Visualizar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredSincronizacoes.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />

      {/* Snackbar para mensagens de erro */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SincronizacaoTable;
